// v56.11: Comprehensive AuditCalculations with flexible signatures

export interface AuditData {
    title: string;
    description: string;
    type: 'time' | 'cost' | 'alert' | 'info' | 'financial' | 'logic';
    formula?: string;
    key?: string;
    value?: any;
}

// Helper to create audit data
const createAudit = (title: string, description: string, type: AuditData['type'], formula?: string): AuditData => ({
    title,
    description,
    type,
    formula
});

export const AuditDictionary = {
    // Timing - All params optional for flexibility
    safeDispatch: (totalDeadline?: number, elapsedMinutes?: number, adjustedTravelTime?: number, tolerance?: number, kitchenRemaining?: number, serviceTime?: number): AuditData =>
        createAudit('Despacho Seguro', `Limite: ${totalDeadline ?? 0}min | Viagem: ${(adjustedTravelTime ?? 0).toFixed(1)}min | Serviço: ${serviceTime ?? 0}min`, 'time', totalDeadline ? `${totalDeadline} - ${elapsedMinutes ?? 0} - ${adjustedTravelTime ?? 0} = ${((totalDeadline ?? 0) - (elapsedMinutes ?? 0) - (adjustedTravelTime ?? 0)).toFixed(1)}min` : undefined),

    arrivalEstimate: (eta?: number, distance?: number, driverSpeed?: number): AuditData =>
        createAudit('Estimativa de Chegada', `ETA: ${(eta ?? 0).toFixed(0)}min | Distância: ${(distance ?? 0).toFixed(1)}km`, 'time', driverSpeed ? `${distance}km ÷ ${driverSpeed}km/h` : undefined),

    // Cost - All params optional
    costBreakdown: (costOwn?: number, costThird?: number, delta?: number): AuditData =>
        createAudit('Comparativo de Custo', `Próprio: R$${(costOwn ?? 0).toFixed(2)} | Terceiro: R$${(costThird ?? 0).toFixed(2)}`, 'cost', `Delta: R$${Math.abs(delta ?? 0).toFixed(2)} ${(delta ?? 0) > 0 ? '(Economia)' : '(Custo Extra)'}`),

    smartSavings: (savedAmount?: number, altCost?: number, strategy?: string): AuditData =>
        createAudit('Economia Inteligente', strategy ? `Estratégia: ${strategy}` : `Custo: R$${(savedAmount ?? 0).toFixed(2)}`, 'cost', altCost !== undefined ? `Alternativa: R$${altCost.toFixed(2)}` : `R$${(savedAmount ?? 0).toFixed(2)} economizados`),

    // Financial - All params optional
    fleetProductivity: (deliveries?: number, avgDistance?: number): AuditData =>
        createAudit('Produtividade da Frota', `${deliveries ?? 0} entregas${avgDistance ? ` | Média: ${avgDistance.toFixed(1)}km` : ''}`, 'financial'),

    revenueGross: (products?: number, fees?: number, gross?: number): AuditData =>
        createAudit('Receita Bruta', `Produtos: R$${(products ?? 0).toFixed(2)} | Taxas: R$${(fees ?? 0).toFixed(2)}`, 'financial', gross ? `Total: R$${gross.toFixed(2)}` : undefined),

    cashFlowPhysical: (cashIn?: number, cashOut?: number, balance?: number): AuditData =>
        createAudit('Fluxo de Caixa Físico', `Entrada: R$${(cashIn ?? 0).toFixed(2)} | Saída: R$${(cashOut ?? 0).toFixed(2)}`, 'financial', balance !== undefined ? `Saldo: R$${balance.toFixed(2)}` : undefined),

    driverSettlement: (cashToCollect?: number, totalSangria?: number, driverName?: string): AuditData =>
        createAudit('Acerto do Motoboy', driverName ? `${driverName}` : `Dinheiro: R$${(cashToCollect ?? 0).toFixed(2)}`, 'financial', `Recolhido: R$${(totalSangria ?? 0).toFixed(2)} | Pendente: R$${((cashToCollect ?? 0) - (totalSangria ?? 0)).toFixed(2)}`),

    // Logic - First param accepts string (reason) for backwards compatibility
    readiness: (reasonOrIsReady?: string | boolean, reason?: string): AuditData => {
        const isString = typeof reasonOrIsReady === 'string';
        return createAudit('Status de Prontidão', isString ? reasonOrIsReady : (reason ?? 'Verificando...'), 'logic', isString ? undefined : (reasonOrIsReady ? 'PRONTO' : 'AGUARDANDO'));
    },

    // Generic - Required: title, description; Optional: type
    generic: (title: string, description: string, type: AuditData['type'] = 'info'): AuditData =>
        createAudit(title, description, type)
};
